import { useEffect, useState } from "react";
import axios from "axios";
import { MARKETS } from "./content";
import { WHITE_COLLAR, BLUE_COLLAR } from "./workforce";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = {
  services: [], hidden_services: [],
  markets: [], hidden_markets: [],
  workforce: [], hidden_workforce: [],
  overrides: { service: {}, market: {}, workforce: {} },
};

// The public CMS payload is tiny and identical for every visitor — fetch it once per page load.
let cache = null;
export const fetchCms = () => {
  if (!cache) cache = axios.get(`${API}/cms`).then((r) => ({ ...EMPTY, ...r.data })).catch(() => EMPTY);
  return cache;
};

export const useCms = () => {
  const [cms, setCms] = useState(EMPTY);
  useEffect(() => {
    let live = true;
    fetchCms().then((d) => live && setCms(d));
    return () => { live = false; };
  }, []);
  return cms;
};

const ov = (cms, kind, slug) => cms.overrides?.[kind]?.[slug] || null;

// Built-in services minus the ones hidden in the CMS, plus overrides and custom services.
export const applyServiceCms = (services, cms) => [
  ...services
    .filter((s) => !cms.hidden_services.includes(s.id))
    .map((s) => {
      const o = ov(cms, "service", s.id);
      if (!o) return s;
      return {
        ...s,
        title: o.title || s.title,
        summary: o.summary || s.summary,
        intro: o.summary || s.intro,
        support: o.support?.length ? o.support : s.support,
      };
    }),
  ...cms.services.map((c) => ({
    id: c.slug, category: c.category || "consultancy", icon: c.icon || "ShieldCheck",
    title: c.title, summary: c.summary, intro: c.summary, support: c.support || [], custom: true,
  })),
];

export const useMarkets = () => {
  const cms = useCms();
  return [
    ...MARKETS.filter((mk) => !cms.hidden_markets.includes(mk.code)).map((mk) => {
      const o = ov(cms, "market", mk.code);
      if (!o) return mk;
      return {
        ...mk,
        mapName: o.title || mk.mapName,
        note: o.note || mk.note,
        whatsapp: o.whatsapp || mk.whatsapp,
        email: o.email || mk.email,
        city: o.city || mk.city,
      };
    }),
    ...cms.markets.map((c) => ({
      code: c.slug, mapName: c.title, coords: [c.lng, c.lat],
      whatsapp: c.whatsapp || MARKETS[0].whatsapp, email: c.email, city: c.city,
      hq: false, note: c.note, custom: true,
    })),
  ];
};

// Single market (built-in or custom) with CMS overrides applied — used by /markets/:slug.
export const useMarket = (code) => useMarkets().find((m) => m.code === code);

// Workforce groups (white/blue collar) with CMS hide/override/custom support.
export const useWorkforceGroups = (kind) => {
  const cms = useCms();
  const base = kind === "blue" ? BLUE_COLLAR : WHITE_COLLAR;
  return [
    ...base.filter((g) => !cms.hidden_workforce.includes(g.id)).map((g) => {
      const o = ov(cms, "workforce", g.id);
      if (!o) return g;
      return { ...g, title: o.title || g.title, roles: o.items?.length ? o.items : g.roles };
    }),
    ...cms.workforce
      .filter((c) => (c.category || "white") === kind)
      .map((c) => ({ id: c.slug, icon: c.icon || "HardHat", title: c.title, roles: c.items || [], custom: true })),
  ];
};
